// Computed slices over the board.
//
// The distinction that matters: a SECTOR is something a person decides (who
// walks which street, which team owns which block) and is stored. A SLICE is
// derived from data already on the row — who has medical needs, who is over
// 70, who is still unaccounted. Storing those as assignable groups would mean
// hand-maintaining what the system can compute, and the hand-kept copy goes
// stale the moment somebody's status changes. So slices are evaluated live,
// every time, and are never wrong.
//
// Both are printable in the same packet format.

import type { IncidentPerson, IncidentSite } from './api';

export interface Slice {
  id: string;
  label: string;
  description: string;
  /** A site is in the slice if any occupant matches, or the site itself does. */
  match: (site: IncidentSite) => boolean;
}

const anyPerson = (f: (p: IncidentPerson) => boolean) => (s: IncidentSite) => s.people.some(f);

export const SLICES: Slice[] = [
  {
    id: 'unaccounted',
    label: 'Unaccounted',
    description: 'Anyone not yet confirmed safe — the working list during a sweep.',
    match: anyPerson((p) => p.accounted === 'unknown' || p.accounted === 'missing'),
  },
  {
    id: 'unswept',
    label: 'Not yet visited',
    description: 'Addresses nobody has reached this round.',
    match: (s) => !s.swept_this_round,
  },
  {
    id: 'medical',
    label: 'Medical & special needs',
    description: 'Oxygen, mobility, cognitive, medication — people who need checking first.',
    match: anyPerson((p) => !!p.medical_needs || p.special_needs.length > 0),
  },
  {
    id: 'seniors',
    label: 'Seniors (70+)',
    description: 'Older residents, who are disproportionately affected and often alone.',
    match: anyPerson((p) => p.age != null && p.age >= 70),
  },
  {
    id: 'children',
    label: 'Households with children',
    description: 'Anyone under 18 on the roster.',
    match: anyPerson((p) => p.age != null && p.age < 18),
  },
  {
    id: 'injured',
    label: 'Injured',
    description: 'Anyone triaged immediate or delayed.',
    match: anyPerson((p) => p.triage === 'red' || p.triage === 'yellow'),
  },
  {
    id: 'needs',
    label: 'Unmet needs',
    description: 'Water, power, shelter, transport — feeds the resource request list.',
    match: anyPerson((p) => p.needs.length > 0),
  },
  {
    id: 'hazards',
    label: 'Hazards & unsafe structures',
    description: 'Gas, fire, structural damage, or a red placard.',
    match: (s) => s.hazards.length > 0 || s.structure === 'red' || s.structure === 'yellow',
  },
  {
    id: 'alone',
    label: 'Living alone',
    description: 'Single-occupant households — nobody there to raise the alarm for them.',
    match: (s) => s.people.length === 1,
  },
];

export const sliceById = (id: string | null | undefined): Slice | undefined =>
  SLICES.find((s) => s.id === id);

/** Layer keys in use across the board, so the UI never hardcodes them. */
export function layersOf(sites: IncidentSite[]): string[] {
  const keys = new Set<string>();
  for (const s of sites) for (const k of Object.keys(s.sectors ?? {})) keys.add(k);
  // The ward's own two layers lead, largest division first: an incident is
  // divided between leaders by macro sector and then walked by micro sector,
  // so that is the order someone reads the board in.
  const rank = (k: string) => (k === 'macro' ? 0 : k === 'micro' ? 1 : k === 'geo' ? 2 : 3);
  return [...keys].sort((a, b) => rank(a) - rank(b) || a.localeCompare(b));
}

export const LAYER_LABELS: Record<string, string> = {
  macro: 'Ward sector',
  micro: 'Walk',
  geo: 'Geographic',
  block: 'Grid block',
  team: 'Team',
  shift: 'Shift',
};

export const layerLabel = (k: string) =>
  LAYER_LABELS[k] ?? k.charAt(0).toUpperCase() + k.slice(1);
